import { fail, ok, type Result } from '../result.js';
import { BASES_LGC, CAIXA_DE, CAIXA_PARA, COMPOSTOS_NOVOS, CONFUNDIVEIS_V5_DE, CONFUNDIVEIS_V5_PARA, DOBRA_DE, DOBRA_PARA, ESPACOS, MARCAS, MARCAS_NOVAS, MARCAS_SOLTAS } from './confundiveis.generated.js';

/*
 * Normalização única do texto de entrada.
 *
 * Review r8 robustez (B4): nome só com caracteres invisíveis (U+200B zero-width, U+FEFF BOM, marcas de direção)
 * passava pelo `trim` e pela checagem de vazio, e aparecia na tela como um item sem nome. Toda entrada de texto
 * passa a ser NFC e perde os invisíveis das bordas.
 *
 * Review r9 robustez (B2): o invisível era só a categoria Cf, e passavam nomes visualmente vazios com U+3164 e
 * U+FFA0 (preenchimentos Hangul), U+115F e U+1160, U+2800 (Braille em branco), U+034F, U+1D159 e só tags. Agora é
 * invisível todo `Default_Ignorable_Code_Point` (inclui os preenchimentos Hangul, seletores de variação e U+034F),
 * formato (Cf), controle (Cc, fora os espaços) e U+2800 e U+1D159. As tags Unicode (U+E0000 a U+E007F) só valem
 * depois de um caractere visível (fecham bandeiras como a da Escócia no fim de um nome); no começo, ou sozinhas, saem.
 * O mesmo vale para os seletores de variação (U+FE0F do emoji colorido).
 *
 * Review r10 robustez (B4): U+16FE4 (KHITAN SMALL SCRIPT FILLER, categoria Mn, fora do Default_Ignorable) passava
 * como nome visualmente vazio; entra na classe junto com U+303F (IDEOGRAPHIC HALF FILL SPACE, So, desenha um espaço).
 * Conferido contra o Unicode do Node (17): os intervalos da classe do SQL (0025 e 0026) são exatamente
 * Default_Ignorable + Cf + Cc (U+180B a U+180F, U+1D173 a U+1D17A, U+E0000 a U+E0FFF, preenchimentos Hangul e
 * U+034F incluídos) mais U+2800, U+1D159, U+16FE4 e U+303F; ver `packages/shared/tests/invisiveis-r10-robustez.test.ts`.
 * A mesma classe serve à recusa no meio dos nomes de identidade (`nomeSemControle`) e ao esqueleto (`esqueletoDeNome`).
 */
const RE_INVISIVEL = /[\p{Default_Ignorable_Code_Point}\p{Cf}\p{Cc}\u2800\u{1D159}\u303F]|\u{16FE4}/u;
const RE_ESPACO = /\s/u;
/** Controles que são espaço (tab, quebra de linha): ficam com o `trim` e no meio do texto. */
const RE_CONTROLE_ESPACO = /[\t\n\v\f\r]/;
/** Controle de espaço e quebra de linha no meio de um nome (review r11 segurança, obs1). */
const RE_QUEBRA_NO_NOME = /[\t\n\v\f\r\u0085\u2028\u2029]/;
/** Só ASCII visível e espaços comuns: nada a limpar. */
const RE_PRECISA_LIMPAR = /[^\x20-\x7E\t\n\r]/;

/** Tags (bandeiras) e seletores de variação (emoji colorido): no fim, depois de um caractere visível, ficam. */
const ehSufixoDeEmoji = (cp: string) => {
  const c = cp.codePointAt(0)!;
  return (c >= 0xe0000 && c <= 0xe007f) || (c >= 0xfe00 && c <= 0xfe0f) || (c >= 0xe0100 && c <= 0xe01ef);
};

/** Invisível: ignorável, formato, controle (fora os espaços), U+2800 e U+1D159. */
export function ehInvisivel(cp: string): boolean {
  return RE_INVISIVEL.test(cp) && !RE_CONTROLE_ESPACO.test(cp);
}

const ehBorda = (cp: string) => ehInvisivel(cp) || RE_ESPACO.test(cp);

/**
 * NFC e sem os invisíveis das bordas, mesmo misturados com espaço. Os espaços comuns ficam onde estão (quem
 * corta espaço é o `trim` de cada campo): " \u200B abc " vira "  abc ". Texto só com ASCII visível volta como veio.
 * Tags e seletores de variação no fim ficam, porque vêm depois de um caractere visível (bandeira, emoji colorido).
 */
export function limparBordasInvisiveis(bruto: string): string {
  if (!RE_PRECISA_LIMPAR.test(bruto)) return bruto;
  const s = bruto.normalize('NFC');
  const cps = Array.from(s);
  let ini = 0;
  while (ini < cps.length && ehBorda(cps[ini]!)) ini++;
  let fim = cps.length;
  while (fim > ini && ehBorda(cps[fim - 1]!) && !ehSufixoDeEmoji(cps[fim - 1]!)) fim--;
  const esquerda = cps.slice(0, ini);
  const direita = cps.slice(fim);
  if (!esquerda.some(ehInvisivel) && !direita.some(ehInvisivel)) return s;
  const soEspaco = (xs: string[]) => xs.filter((c) => !ehInvisivel(c)).join('');
  return soEspaco(esquerda) + cps.slice(ini, fim).join('') + soEspaco(direita);
}

/** Texto de entrada normalizado: NFC, sem invisíveis nem espaços nas bordas. Só invisíveis e espaço vira ''. */
export function normalizarTexto(bruto: string): string {
  return limparBordasInvisiveis(bruto).trim();
}

/** Vazio para as checagens de obrigatório: null, undefined, '' ou texto só com espaço e invisíveis. */
export function ehTextoVazio(v: unknown): boolean {
  return v === undefined || v === null || (typeof v === 'string' && normalizarTexto(v) === '');
}

export function textoObrigatorio(campo: string, bruto: unknown, max = 200): Result<string> {
  const v = typeof bruto === 'string' ? normalizarTexto(bruto) : '';
  if (!v) return fail(`${campo}.obrigatorio`, `Campo ${campo} é obrigatório`, { campo });
  if (v.length > max) return fail(`${campo}.muito_longo`, `Campo ${campo} passa de ${max} caracteres`, { campo, max });
  return ok(v);
}


const ZWJ = 0x200d;
const ZWNJ = 0x200c;
const BANDEIRA_PRETA = 0x1f3f4;
const TAG_FIM = 0xe007f;
const ehSeletorVariacao = (c: number) => (c >= 0xfe00 && c <= 0xfe0f) || (c >= 0xe0100 && c <= 0xe01ef);
const ehTag = (c: number) => c >= 0xe0020 && c <= 0xe007f;
const RE_BASE_DE_SELETOR = /[\p{Emoji}\p{Script=Han}]/u;
/** Caractere que aparece na tela: nem invisível nem espaço. */
const ehVisivel = (cp: string | undefined) => cp !== undefined && !ehInvisivel(cp) && !RE_ESPACO.test(cp);

/**
 * Posições de invisível com uso ortográfico ou de emoji (review r10 spec, B2), que o nome de identidade aceita:
 *  - ZWJ e ZWNJ entre dois caracteres que aparecem (persa `مهدی‌زاده`, cingalês `ශ්‍රී`, emoji `👩‍💻`), contando como
 *    "aparece" também o seletor de variação já aceito antes dele (`❤️‍🔥`);
 *  - seletor de variação logo depois de emoji ou de ideograma Han (emoji colorido, variante ideográfica);
 *  - tags de bandeira depois de U+1F3F4, até a tag de fim U+E007F (`🏴󠁧󠁢󠁳󠁣󠁴󠁿`).
 * Todo o resto continua recusado: bidi (RLO, LRO, RLE, LRE, PDF, LRI, RLI, FSI, PDI), U+200B, U+2060, U+FEFF, LRM e
 * RLM, C0 e C1, U+034F e os preenchimentos (U+3164, U+FFA0, U+115F, U+1160, U+2800).
 */
function invisivelPermitidoEm(cps: string[], i: number, aceitos: boolean[]): boolean {
  const c = cps[i]!.codePointAt(0)!;
  const ant = cps[i - 1];
  const antCp = ant?.codePointAt(0);
  if (c === ZWJ || c === ZWNJ) {
    const antAparece = ehVisivel(ant) || (antCp !== undefined && ehSeletorVariacao(antCp) && aceitos[i - 1] === true);
    return antAparece && ehVisivel(cps[i + 1]);
  }
  if (ehSeletorVariacao(c)) return ant !== undefined && ehVisivel(ant) && RE_BASE_DE_SELETOR.test(ant);
  if (ehTag(c)) {
    // A sequência de tags começa logo depois da bandeira preta e termina na tag de fim.
    let j = i;
    while (j > 0 && ehTag(cps[j - 1]!.codePointAt(0)!)) j--;
    if (cps[j - 1]?.codePointAt(0) !== BANDEIRA_PRETA) return false;
    let k = i;
    while (k < cps.length && ehTag(cps[k]!.codePointAt(0)!) && cps[k]!.codePointAt(0) !== TAG_FIM) k++;
    return cps[k]?.codePointAt(0) === TAG_FIM && k - j >= 1;
  }
  return false;
}

/**
 * Review r9 segurança (B2): nome que identifica alguém ou um acesso (papel, pessoa na conta, convite, dono do tenant)
 * não aceita caractere de controle, de formato nem outro invisível no meio. Antes só as bordas eram limpas, e
 * `Do\u200Bno` ou `D\u202Eono` viravam um segundo "Dono" visualmente igual ao de fábrica. Review r10 spec (B2): a
 * recusa da classe inteira barrava nomes legítimos; agora ZWJ, ZWNJ, seletor de variação e tags valem nas posições
 * de `invisivelPermitidoEm`. O esqueleto (`esqueletoDeNome`) tira todos eles: "Do\u200Dno" é aceito como texto, mas
 * colide com "Dono" na unicidade do papel. Mesmas regras de `textoObrigatorio`, mais esta.
 */
export function nomeSemControle(campo: string, bruto: unknown, max = 200): Result<string> {
  const v = textoObrigatorio(campo, bruto, max);
  if (!v.ok) return v;
  // Review r11 segurança (obs1): tab, quebra de linha (CR, LF, VT, FF, NEL) e os separadores de linha e parágrafo são C0,
  // C1 ou Zl/Zp e não cabem num nome; `ehInvisivel` os deixava passar por serem espaço. O nome do workspace com CR/LF
  // ia cru ao assunto do convite.
  if (RE_QUEBRA_NO_NOME.test(v.valor)) return fail(`${campo}.caractere_invalido`, `Campo ${campo} tem caractere invisível ou de controle no meio do texto`, { campo });
  if (RE_INVISIVEL.test(v.valor)) {
    const cps = Array.from(v.valor);
    const aceitos: boolean[] = [];
    for (let i = 0; i < cps.length; i++) {
      if (!ehInvisivel(cps[i]!)) {
        aceitos.push(true);
        continue;
      }
      if (!invisivelPermitidoEm(cps, i, aceitos)) {
        return fail(`${campo}.caractere_invalido`, `Campo ${campo} tem caractere invisível ou de controle no meio do texto`, { campo });
      }
      aceitos.push(true);
    }
  }
  return v;
}

/**
 * Review r10 segurança (B1) e r11 segurança (B3): confundíveis da UTS #39 (`confusables.txt`) que se passam por letra ou
 * dígito ASCII. A r10 tinha uma tabela reduzida (246 origens) e passavam 380 da UTS #39 (cirílico U+1C82, armênio
 * U+057C, latino U+026F, osage, itálico antigo, hangul, mianmar). Agora a tabela é a COMPLETA, gerada por
 * `scripts/gerar-confundiveis.mjs` em `confundiveis.generated.ts`, junto com a caixa (minúscula por tabela, a mesma do
 * SQL), as marcas combinantes e os espaços. Letra ASCII nunca é trocada (senão "I" e "l" deixariam de ser só maiúscula e
 * minúscula). O destino é minúsculo. A mesma tabela está na função SQL `nucleo_esqueleto_papel_v5` (migration 0032),
 * conferida em teste (`apps/api/tests/esqueleto-r12.int.test.ts`). Review r12 segurança (B1): entra também o ponto cujo
 * protótipo de várias letras é o de uma letra ASCII ("rn" por "m": U+0560, U+118E3, U+11700).
 */
function mapaDePares(de: string, para: string): Map<string, string> {
  const d = [...de];
  const p = [...para];
  if (d.length !== p.length) throw new Error('tabela gerada com origem e destino de tamanhos diferentes');
  return new Map(d.map((c, i) => [c, p[i]!]));
}
const classeDeIntervalos = (ints: readonly (readonly [number, number])[]) =>
  ints.map(([a, b]) => (a === b ? `\\u{${a.toString(16)}}` : `\\u{${a.toString(16)}}-\\u{${b.toString(16)}}`)).join('');

const CONFUNDIVEIS = mapaDePares(CONFUNDIVEIS_V5_DE, CONFUNDIVEIS_V5_PARA);
const CAIXA = mapaDePares(CAIXA_DE, CAIXA_PARA);
const DOBRA = mapaDePares(DOBRA_DE, DOBRA_PARA);
/** Invisíveis: saem depois do NFD (a mesma classe do SQL). */
const RE_INVISIVEL_TODOS = new RegExp(RE_INVISIVEL.source, 'gu');
/** Toda marca (\p{M} do Unicode da geração): sai na conferência dos nomes reservados. */
const RE_MARCA = new RegExp(`[${classeDeIntervalos(MARCAS)}]`, 'gu');
/**
 * Review r12 spec (B2): marca SOLTA (uma só sobre a letra) sobre letra latina, grega, cirílica ou comum, fora as
 * vietnamitas (U+0309, U+031B, U+0323): é o acento, e sai. A mesma expressão está na função SQL v5.
 */
const RE_MARCA_SOLTA = new RegExp(`(?<=[${classeDeIntervalos(BASES_LGC)}])[${classeDeIntervalos(MARCAS_SOLTAS)}](?![${classeDeIntervalos(MARCAS)}])`, 'gu');
const RE_ESPACOS_GERADOS = new RegExp(`[${classeDeIntervalos(ESPACOS)}]+`, 'gu');
/**
 * Review r12 (v5): composto canônico novo para o Postgres 16 (Unicode acima do 15.0) e a decomposição dele. A v5 mantém
 * marcas, e o NFC do Node recomporia a sequência que o do Postgres deixa decomposta: aqui ela volta a ficar decomposta
 * (o SQL troca o composto pela decomposição antes do NFKC).
 */
const DECOMPOSTOS = new Map(COMPOSTOS_NOVOS.map(([cp, d]) => [String.fromCodePoint(cp), String.fromCodePoint(...d)]));
/**
 * O composto novo com as marcas que vêm logo depois dele. Review r13 segurança (B3): a decomposição entrava no lugar sem
 * reordenar as marcas seguintes. Com marca de classe combinante MENOR depois do composto (U+3099 classe 8, U+0591,
 * U+0F71, U+0E48), o Postgres (que não conhece o composto) deixa "base, marca menor, U+0307" na ordem canônica; o JS
 * recompunha no NFC (a marca menor não bloqueia) e, decompondo no lugar, ficava com "base, U+0307, marca menor".
 */
const RE_COMPOSTO_NOVO = new RegExp(`[${COMPOSTOS_NOVOS.map(([cp]) => `\\u{${cp.toString(16)}}`).join('')}]\\p{M}*`, 'gu');
/** Marca nova para o Postgres 16 (Unicode acima do 15.0): sai antes do NFKC (o Postgres não sabe a classe combinante dela). */
const RE_MARCA_NOVA = new RegExp(`[${classeDeIntervalos(MARCAS_NOVAS)}]`, 'gu');
/**
 * Troca o composto novo pela decomposição e reaplica a ordem canônica no aglomerado (decomposição mais as marcas
 * seguintes) com o NFD dele: sem composto a refazer, o NFD só reordena as marcas por classe combinante, como o NFKC/NFC
 * do Postgres faz com a sequência que ele não sabe compor.
 */
const decomporNovos = (s: string) =>
  COMPOSTOS_NOVOS.length
    ? s.replace(RE_COMPOSTO_NOVO, (x) => {
        const composto = String.fromCodePoint(x.codePointAt(0)!);
        return ((DECOMPOSTOS.get(composto) ?? composto) + x.slice(composto.length)).normalize('NFD');
      })
    : s;

/** De e para, na mesma ordem (um ponto de código por posição), para o `translate` da função SQL. */
export const CONFUNDIVEIS_DE = CONFUNDIVEIS_V5_DE;
export const CONFUNDIVEIS_PARA = CONFUNDIVEIS_V5_PARA;

const trocar = (s: string, m: Map<string, string>) => {
  let saida = '';
  for (const c of s) saida += m.get(c) ?? c;
  return saida;
};

/**
 * Minúsculas por tabela (a mesma de `nucleo_caixa_v1` no SQL, 0027): não depende do locale do banco nem do Unicode do
 * runtime. Review r11 spec (B4): base da chave canônica do e-mail de contato.
 */
export const baixarCaixa = (s: string) => trocar(s, CAIXA);

/**
 * Esqueleto do nome para a unicidade (review r9 segurança, B2; r10 segurança, B1; r11 segurança, B3 e obs2; r12
 * segurança B1 e spec B2). Mesma ordem da função SQL `nucleo_esqueleto_papel_v5` (0032), que a coluna gerada
 * `papeis.nome_esqueleto5` guarda e o índice `papeis_nome_esqueleto5_unico` compara:
 *  1. confundíveis (a maiúscula com desenho próprio, como Η por "h", e o que o NFKC levaria a outra letra, como ϲ);
 *  2. caixa por tabela, sem `toLowerCase` (o `lower()` do Postgres depende do locale; a tabela é a mesma nos dois);
 *  3. confundíveis de novo (a maiúscula que não estava na tabela e cuja minúscula está: Ɡ, U+A7AC, vira ɡ e "g") e a
 *     dobra (caractere do Unicode acima do 15.0, que o `normalize()` do Postgres 16 não conhece, já normalizado por tabela);
 *  4. NFKC e caixa de novo (o NFKC devolve maiúscula: letra matemática, largura total);
 *  5. NFD, sem invisíveis e sem a marca solta sobre letra latina, grega, cirílica ou comum, NFC ("Gestão" e "Gestao"
 *     colidem; "Admı̇nistrador" perde o U+0307; "शिक्षक" e "शिक्षिका", "パート" e "ハート", "Thủ quỹ" e "Thu quy" não);
 *  6. confundíveis de novo (o que só aparece com o NFKC ou sem as marcas: ϱ, ı);
 *  7. espaços reduzidos a um e bordas cortadas.
 */
export function esqueletoDeNome(nome: string): string {
  return esqueleto(nome, false);
}

function esqueleto(nome: string, todasAsMarcas: boolean): string {
  const passo3 = trocar(trocar(trocar(trocar(nome, CONFUNDIVEIS), CAIXA), CONFUNDIVEIS), DOBRA);
  const passo4 = trocar(decomporNovos(passo3).replace(RE_MARCA_NOVA, '').normalize('NFKC'), CAIXA);
  const semInvisiveis = passo4.normalize('NFD').replace(RE_INVISIVEL_TODOS, '');
  const passo5 = decomporNovos(semInvisiveis.replace(todasAsMarcas ? RE_MARCA : RE_MARCA_SOLTA, '').normalize('NFC'));
  return trocar(passo5, CONFUNDIVEIS).replace(RE_ESPACOS_GERADOS, ' ').replace(/^ +| +$/g, '');
}

/**
 * Review r12 spec (B2): a conferência dos nomes reservados segue tirando TODAS as marcas (como a v4): "Ạdministrador"
 * (ponto embaixo), "Administrador" com acento empilhado ou com marca de outra escrita sobre a letra, e a marca solta no
 * começo, seguem se passando pelo papel de fábrica e dão 409. Só a unicidade entre papéis personalizados distingue as
 * marcas que a v5 mantém.
 */
const esqueletoReservado = (nome: string) => esqueleto(nome, true);

/**
 * Review r10 segurança (B1): nomes dos papéis de fábrica nos três idiomas do app (os seletores mostram o papel de
 * fábrica traduzido, sem selo). Um papel personalizado "Owner" ou "Propietario" ficava idêntico ao Dono em en ou es.
 * Mesmos textos de `papelFabrica` no i18n do web (admin, pessoas e integrações).
 */
export const NOMES_PAPEIS_DE_FABRICA: readonly string[] = [
  'Dono',
  'Administrador',
  'Gestor',
  'Operador',
  'Leitura',
  'Owner',
  'Administrator',
  'Manager',
  'Operator',
  'Read only',
  'Propietario',
  'Solo lectura',
  // crm6 item 9 (29/10): o papel de código `dono` passou a se chamar TI (pt/es) e IT (en) na tela.
  'TI',
  'IT',
];
const ESQUELETOS_RESERVADOS = new Set(NOMES_PAPEIS_DE_FABRICA.map(esqueletoReservado));

/** O nome se passa por um papel de fábrica em algum idioma (pt, en, es)? Compara pelo esqueleto. */
export function nomeReservadoDePapel(nome: string): boolean {
  return ESQUELETOS_RESERVADOS.has(esqueletoReservado(nome));
}

export function textoOpcional(campo: string, bruto: unknown, max = 2000): Result<string | null> {
  if (bruto === null || bruto === undefined || bruto === '') return ok(null);
  if (typeof bruto !== 'string') return fail(`${campo}.invalido`, `Campo ${campo} inválido`, { campo });
  const v = normalizarTexto(bruto);
  if (v.length > max) return fail(`${campo}.muito_longo`, `Campo ${campo} passa de ${max} caracteres`, { campo, max });
  return ok(v || null);
}

export function slugificar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}
